import React, { useState, useEffect } from 'react';

const API_BASE = import.meta.env.VITE_API_BASE || "https://8kgetter.jon-13b.workers.dev";

// Mobile-first responsive styles
const cardStyles: React.CSSProperties = {
  width: "100%",
  maxWidth: "520px",
  background: "#0f172a",
  padding: "16px",
  borderRadius: "12px",
  boxShadow: "0 10px 30px rgba(0,0,0,.35)",
  margin: "0 auto"
};

const containerStyles: React.CSSProperties = {
  minHeight: "100dvh", // Dynamic viewport height for mobile
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "#0b1220",
  color: "#e6edf3",
  padding: "16px",
  boxSizing: "border-box"
};

const inputStyles: React.CSSProperties = {
  width: "100%",
  padding: "12px 16px",
  borderRadius: "8px",
  border: "1px solid #26304a",
  background: "#0b1327",
  color: "#e6edf3",
  fontSize: "16px", // Prevents zoom on iOS
  marginTop: "8px",
  marginBottom: "16px",
  boxSizing: "border-box"
};

const buttonStyles: React.CSSProperties = {
  width: "100%",
  padding: "14px",
  borderRadius: "8px",
  border: "none",
  fontSize: "16px",
  fontWeight: "600",
  cursor: "pointer",
  transition: "all 0.2s ease",
  boxSizing: "border-box"
};

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div style={cardStyles}>
      {children}
    </div>
  );
}

interface DashboardProps {
  user: { phone: string; token: string };
  onLogout: () => void;
}

export const Dashboard: React.FC<DashboardProps> = ({ user, onLogout }) => {
  const [tickers, setTickers] = useState("");
  const [phone, setPhone] = useState("");
  const [formTypes, setFormTypes] = useState<string[]>(["8-K"]);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [message, setMessage] = useState("");
  
  // Check if tickers are entered
  const hasValidTickers = tickers.trim().length > 0;

  const loadSettings = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/settings`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setTickers(data.tickers?.join(", ") || "");
        setPhone(data.phone || user.phone);
        setFormTypes(data.formTypes || ["8-K"]);
      }
    } catch (error) {
      console.error("Failed to load settings:", error);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);


  async function save(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    try {
      const token = user.token;
      const res = await fetch(`${API_BASE}/api/settings`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ tickers, phone, formTypes }),
      });
      const data = await res.json();
      if (res.ok) {
        setMessage("✅ Saved! You'll get SMS alerts for new SEC filings.");
      } else {
        setMessage(`❌ ${data.error || "Save failed"}`);
      }
    } catch (err) {
      setMessage(`❌ Network error: ${err}`);
    }
    setLoading(false);
  }

  async function testRun() {
    setLoading(true);
    setMessage("");
    try {
      const token = user.token;
      const res = await fetch(`${API_BASE}/api/test`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setMessage(`✅ Test complete: ${data.message || "No recent SEC filings found"}`);
      } else {
        setMessage(`❌ ${data.error || "Test failed"}`);
      }
    } catch (err) {
      setMessage(`❌ Test error: ${err}`);
    }
    setLoading(false);
  }

  async function deleteAccount() {
    setDeleteLoading(true);
    setMessage("");
    
    try {
      const token = user.token;
      const res = await fetch(`${API_BASE}/api/delete-account`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setMessage("✅ Account deleted successfully - logging out...");
        setTimeout(() => onLogout(), 2000);
      } else {
        setMessage(`❌ ${data.error || "Failed to delete account"}`);
      }
    } catch (err) {
      setMessage(`❌ Delete error: ${err}`);
    }
    setDeleteLoading(false);
  }

  function signOutAndStartAlerts() {
    // Save current settings first, then logout
    save(new Event('submit') as any).then(() => {
      setTimeout(() => onLogout(), 1000);
    });
  }

  return (
    <div style={containerStyles}>
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", flexWrap: "wrap", gap: "8px" }}>
          <h1 style={{ fontSize: "20px", margin: 0, lineHeight: 1.2 }}>8kgetter.com</h1>
          <button 
            onClick={onLogout} 
            style={{ padding: "8px 16px", borderRadius: "6px", border: "1px solid #27314d", background: "transparent", color: "#e6edf3", fontSize: "14px", minHeight: "36px" }}
          >
            Log out
          </button>
        </div>

        <p style={{ opacity: 0.8, marginTop: 0, fontSize: "14px", lineHeight: 1.4 }}>
          Get 8-Ks and more, right to your phone. Enter tickers below to get SMS alerts for new SEC filings.
        </p>

        <form onSubmit={save}>
          <label style={{ display: "block", fontWeight: 600, fontSize: "14px", marginBottom: "4px" }}>Tickers</label>
          <input
            value={tickers}
            onChange={(e) => setTickers(e.target.value)}
            placeholder="AAPL, MSFT, TSLA"
            style={inputStyles}
          />

          <label style={{ display: "block", fontWeight: 600, fontSize: "14px", marginBottom: "4px" }}>Phone (E.164)</label>
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+14015551234"
            type="tel"
            style={inputStyles}
          />

          <label style={{ display: "block", fontWeight: 600, fontSize: "14px", marginBottom: "8px" }}>Form Types</label>
          <div style={{ marginBottom: "16px", padding: "12px", background: "#1a2332", borderRadius: "8px" }}>
            {[
              { value: "8-K", label: "8-K - Current Report (Material Events)", description: "Major corporate events, acquisitions, executive changes" },
              { value: "10-K", label: "10-K - Annual Report", description: "Comprehensive annual financial report" },
              { value: "10-Q", label: "10-Q - Quarterly Report", description: "Quarterly financial statements" },
              { value: "DEF 14A", label: "DEF 14A - Proxy Statement", description: "Shareholder meeting information, executive compensation" },
              { value: "S-1", label: "S-1 - Registration Statement", description: "IPO registration for new public offerings" },
              { value: "4", label: "Form 4 - Insider Trading", description: "Officer, director, and 10% owner transactions" }
            ].map((form) => (
              <div key={form.value} style={{ marginBottom: "8px" }}>
                <label style={{ display: "flex", alignItems: "flex-start", cursor: "pointer", fontSize: "13px" }}>
                  <input
                    type="checkbox"
                    checked={formTypes.includes(form.value)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormTypes([...formTypes, form.value]);
                      } else {
                        setFormTypes(formTypes.filter(t => t !== form.value));
                      }
                    }}
                    style={{ marginRight: "8px", marginTop: "2px", minWidth: "16px" }}
                  />
                  <div>
                    <div style={{ fontWeight: 600, color: "#e6edf3", fontSize: "13px" }}>{form.label}</div>
                    <div style={{ opacity: 0.7, fontSize: "11px", lineHeight: 1.3 }}>{form.description}</div>
                  </div>
                </label>
              </div>
            ))}
          </div>

          <div style={{ fontSize: "12px", opacity: 0.7, marginBottom: "16px", padding: "12px", background: "#1a2332", borderRadius: "8px", lineHeight: 1.4 }}>
            By providing your phone number, you consent to receive SMS alerts about SEC filings. 
            Message frequency varies. Standard SMS rates apply. Reply STOP to opt out.
          </div>

          <button 
            type="submit" 
            disabled={loading || !hasValidTickers}
            style={{ ...buttonStyles, background: loading || !hasValidTickers ? "#374151" : "#22c55e", color: loading || !hasValidTickers ? "#9ca3af" : "black", fontWeight: "700", marginBottom: "12px", opacity: loading || !hasValidTickers ? 0.6 : 1 }}
          >
            {loading ? "Saving..." : hasValidTickers ? "Save Watchlist" : "Enter Tickers First"}
          </button>
        </form>

        <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginTop: "12px" }}>
          <button 
            disabled={loading || !hasValidTickers} 
            onClick={testRun} 
            style={{ ...buttonStyles, border: "1px solid #27314d", background: "transparent", color: loading || !hasValidTickers ? "#6b7280" : "#e6edf3", fontWeight: "700", opacity: loading || !hasValidTickers ? 0.6 : 1, margin: 0 }}
          >
            {loading ? "Testing..." : hasValidTickers ? "Run Test Now" : "Enter Tickers First"}
          </button>
          
          <div style={{ display: "flex", gap: "8px" }}>
            <button 
              disabled={loading || !hasValidTickers} 
              onClick={() => {
                save(new Event('submit') as any);
              }}
              style={{ ...buttonStyles, flex: 1, background: loading || !hasValidTickers ? "#374151" : "#22c55e", color: loading || !hasValidTickers ? "#9ca3af" : "black", fontWeight: "700", opacity: loading || !hasValidTickers ? 0.6 : 1, margin: 0 }}
            >
              {loading ? "Saving..." : hasValidTickers ? "Save Only" : "Enter Tickers First"}
            </button>
            
            <button 
              disabled={loading || !hasValidTickers} 
              onClick={signOutAndStartAlerts} 
              style={{ ...buttonStyles, flex: 1, background: loading || !hasValidTickers ? "#374151" : "#3b82f6", color: loading || !hasValidTickers ? "#9ca3af" : "white", fontWeight: "700", opacity: loading || !hasValidTickers ? 0.6 : 1, margin: 0 }}
            >
              {loading ? "Saving & Closing..." : hasValidTickers ? "Save & Close" : "Enter Tickers First"}
            </button>
          </div>
          
          <button 
            disabled={deleteLoading || loading} 
            onClick={() => {
              if (confirm("Are you sure you want to delete your account? This will permanently delete all your watchlist data and cannot be undone.")) {
                deleteAccount();
              }
            }}
            style={{ ...buttonStyles, background: "#ef4444", color: "white", fontWeight: "700", opacity: (deleteLoading || loading) ? 0.6 : 1, margin: 0 }}
          >
            {deleteLoading ? "Deleting..." : "Delete Account"}
          </button>
        </div>

        {message && (
          <div style={{ marginTop: "12px", background: "#11213d", padding: "12px", borderRadius: "8px", fontSize: "14px", lineHeight: 1.4, wordBreak: "break-word" }}>
            {message}
          </div>
        )}

        <div style={{ marginTop: "20px", textAlign: "center", fontSize: "12px", opacity: 0.6 }}>
          <p style={{ margin: "0 0 16px 0" }}>Monitoring SEC EDGAR for all selected SEC filings</p>
          <div style={{ display: "flex", justifyContent: "center", gap: "16px", flexWrap: "wrap" }}>
            <a 
              href="https://8kgetter.jon-13b.workers.dev/terms" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ color: "#3b82f6", textDecoration: "underline", fontSize: "11px" }}
            >
              Terms and Conditions
            </a>
            <a 
              href="https://8kgetter.jon-13b.workers.dev/privacy" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ color: "#3b82f6", textDecoration: "underline", fontSize: "11px" }}
            >
              Privacy Policy
            </a>
          </div>
        </div>
      </Card>
    </div>
  );
};
